import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, Input, Output, ViewEncapsulation, inject } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'
import { PaginationEvent } from './pagination-event'
import { PaginationItemDirective } from './pagination-item/pagination-item.directive'

/**
 * @public
 */
export type PaginationElement = NgElement & WithProperties<PaginationComponent>

/**
 * @public
 */
@Component({
  selector: 'gpei-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: [ './pagination.component.sass' ],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    PaginationItemDirective
  ]
})
export class PaginationComponent {

  private readonly _elementRef = inject(ElementRef)

  private _items: number
  private _pageSize: number
  private _directionalButtons = true
  private _buttonLimit = 5
  private _page = 1
  private _extraParams: Record<string, unknown>

  /**
   * The event emitted when the page has changed
   */
  @Output() pageChange: EventEmitter<PaginationEvent> = new EventEmitter()

  /**
   * Set the current page to the selected page. Emit a PaginationEvent from the pageChange output,
   * containing the newly selected page and the size of the page.
   *
   * @param pendingPage The page from the button that was clicked
   */
  onPageSelected(pendingPage: number): void {
    this.page = pendingPage

    let event = new PaginationEvent('pagechange', {
      bubbles: true,
      cancelable: true,
      page: this.page,
      pageSize: this.pageSize,
      extra: this._extraParams
    })

    this._elementRef.nativeElement.dispatchEvent(event)
    this.pageChange.emit(event)
  }

  /**
   * Determine if a button is within the maximum range of buttons
   * we want to display
   *
   * @param pageNumber Number of the button that will be checked
   *
   * @returns True if the button is within the correct range
   */
  withinMaxRange(pageNumber: number): boolean {
    if (this.buttonLimit <= 0) {
      return true
    }

    let threshold = Math.floor((this.buttonLimit - 1) / 2)
    let startBuffer = 0
    let endBuffer = 0

    let distanceFromStart = this.page - 1
    let distanceFromEnd = this.totalPages - this.page

    if (distanceFromEnd < threshold) {
      startBuffer = threshold - distanceFromEnd
    }

    if (distanceFromStart < threshold) {
      endBuffer = threshold - distanceFromStart
    }

    return (
      pageNumber >= this.page - threshold - startBuffer &&
      pageNumber <= this.page + threshold + endBuffer
    )
  }

  /**
   * The total number of items you want to paginate over
   */
  @Input()
  @HostBinding()
  set items(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseInt(value) :
      value

    if (pendingValue !== this._items) {
      this._items = pendingValue
    }
  }

  get items(): number {
    return this._items
  }

  /**
   * The number of items per page
   */
  @Input()
  @HostBinding()
  set pageSize(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseInt(value) :
      value

    if (pendingValue !== this._pageSize) {
      this._pageSize = pendingValue
    }
  }

  get pageSize(): number {
    return this._pageSize
  }

  /**
   * Toggle the display of the directional buttons
   */
  @Input()
  @HostBinding()
  set directionalButtons(value: boolean | string) {
    let pendingValue = typeof value === 'string' ?
      value.toLowerCase() === 'true' :
      value

    if (pendingValue !== this._directionalButtons) {
      this._directionalButtons = pendingValue
    }
  }

  get directionalButtons(): boolean {
    return this._directionalButtons
  }

  /**
   * Limit the number of buttons that will be displayed at once
   */
  @Input()
  @HostBinding()
  set buttonLimit(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseInt(value) :
      value

    if (pendingValue !== this._buttonLimit) {
      this._buttonLimit = pendingValue
    }
  }

  get buttonLimit(): number {
    return this._buttonLimit
  }

  /**
   * The current page
   */
  @Input()
  @HostBinding()
  set page(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseInt(value) :
      value

    if (pendingValue !== this.page) {
      this._page = pendingValue
    }
  }

  get page(): number {
    return this._page
  }

  @Input()
  @HostBinding()
  set extraParams(value: string | Record<string, unknown>) {
    let pendingValue = typeof value === 'string' ?
      JSON.parse(value) :
      value

    if (pendingValue !== this._extraParams) {
      this._extraParams = pendingValue
    }
  }

  get extraParams(): Record<string, unknown> {
    return this._extraParams
  }

  /**
   * The total number of pages.
   *
   * Calculated by rounding up the number of times it can get a full page worth of results,
   * from the total number of items.
   */
  @Input()
  @HostBinding()
  get totalPages(): number {
    return Math.ceil(this.items / this.pageSize)
  }

  /**
   * An array containing an incrementing number for the page
   */
  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_v, k) => k + 1)
  }
}

/**
 * @public
 */
export const registerPaginationElement = registerCustomElementFactory(PaginationComponent)
